import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
  Divider,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PublishIcon from '@mui/icons-material/Publish';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import normalizeSiteConfig from '../../utils/sitePageNormalizer';

function toSlug(str) {
  return (str || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 80);
}

/**
 * EditorToolbar — Barra de ações do editor de sites multi-agenda (TODO 5.6).
 *
 * Três ações:
 *   Preview  → previewRef.current.refresh() com o config atual (FULL_REFRESH — §9.3)
 *   Salvar   → form.handleSubmit → createAsync (nova) ou updateDraftAsync (edição) → navigate
 *   Publicar → Save implícito + publishPageAsync → snackbar → navigate('/pages')
 *
 * Indicador "Alterações não salvas" quando form.formState.isDirty.
 * P12: sem auto-save.
 *
 * @param {object} form             - instância react-hook-form com siteFormSchema
 * @param {string} pageId           - ID da página (undefined para criação)
 * @param {React.Ref} previewRef    - ref de SitePreview (expõe refresh())
 * @param {function} createAsync       - (data) => Promise<page> — cria nova página (usePages)
 * @param {function} updateDraftAsync  - (id, config) => Promise — de usePages
 * @param {function} publishPageAsync  - (id) => Promise — de usePages
 * @param {boolean}  isUpdatingDraft
 * @param {boolean}  isPublishing
 * @param {function} showSuccess
 * @param {function} showError
 * @param {function} onBack     - callback para navegar para fora (usa safeNavigate do pai)
 */
export default function EditorToolbar({
  form,
  pageId,
  previewRef,
  createAsync,
  updateDraftAsync,
  publishPageAsync,
  isUpdatingDraft = false,
  isPublishing = false,
  showSuccess,
  showError,
  onBack,
}) {
  const navigate = useNavigate();
  const [isPublishingLocally, setIsPublishingLocally] = useState(false);

  const isDirty = form.formState.isDirty;
  const isSaving = isUpdatingDraft;
  const isBusy = isSaving || isPublishing || isPublishingLocally;

  function handlePreview() {
    previewRef.current?.refresh();
  }

  /**
   * Salva a página. Para novas páginas (sem pageId), cria via createAsync.
   * Retorna o pageId efetivo (novo ou existente), ou lança em caso de erro.
   */
  async function doSave(formValues) {
    const dto = normalizeSiteConfig(formValues);

    if (!pageId) {
      // Criação: POST /pages com { type, title, slug, templateId, config }
      if (!createAsync) {
        throw new Error('Não é possível salvar: página ainda não foi criada.');
      }
      const title = formValues.seo?.title || '';
      const created = await createAsync({
        type: formValues.siteType,
        title,
        slug: toSlug(title),
        templateId: formValues.layoutTemplateId || null,
        config: dto,
      });
      return { isNew: true, newPageId: created?.id ?? created };
    }

    // Edição: PUT /pages/{id}/draft
    await updateDraftAsync(pageId, dto);
    return { isNew: false, newPageId: pageId };
  }

  async function handleSave(formValues) {
    try {
      const { isNew, newPageId } = await doSave(formValues);
      form.reset(form.getValues());
      if (isNew) {
        showSuccess?.('Página criada com sucesso!');
        navigate(`/pages/edit/${newPageId}`);
      } else {
        showSuccess?.('Rascunho salvo com sucesso!');
        navigate('/pages');
      }
    } catch (err) {
      showError?.(err?.message || 'Erro ao salvar. Tente novamente.');
    }
  }

  async function handlePublish(formValues) {
    setIsPublishingLocally(true);
    try {
      const { newPageId } = await doSave(formValues);
      await publishPageAsync(newPageId);
      form.reset(form.getValues());
      showSuccess?.('Página publicada com sucesso!');
      navigate('/pages');
    } catch (err) {
      showError?.(err?.message || 'Erro ao publicar. Tente novamente.');
    } finally {
      setIsPublishingLocally(false);
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        flexWrap: 'wrap',
      }}
    >
      {onBack && (
        <Button
          size="small"
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          disabled={isBusy}
        >
          Voltar
        </Button>
      )}

      {isDirty && (
        <Chip
          icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
          label="Alterações não salvas"
          size="small"
          color="warning"
          variant="outlined"
        />
      )}

      <Box sx={{ flex: 1 }} />

      <Tooltip title="Atualiza o preview com o conteúdo atual incluindo campos de texto">
        <span>
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={handlePreview}
            disabled={isBusy}
          >
            Preview
          </Button>
        </span>
      </Tooltip>

      <Divider orientation="vertical" flexItem />

      <Tooltip title="Salva o rascunho. Não afeta a versão publicada.">
        <span>
          <Button
            variant="outlined"
            size="small"
            startIcon={isSaving ? <CircularProgress size={14} /> : <SaveIcon />}
            onClick={form.handleSubmit(handleSave)}
            disabled={isBusy}
          >
            Salvar
          </Button>
        </span>
      </Tooltip>

      <Tooltip title="Salva o rascunho e publica a página imediatamente.">
        <span>
          <Button
            variant="contained"
            size="small"
            color="primary"
            startIcon={
              isPublishing || isPublishingLocally ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <PublishIcon />
              )
            }
            onClick={form.handleSubmit(handlePublish)}
            disabled={isBusy}
          >
            Publicar
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
}
